import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useState, useEffect, useRef } from "react";
import { Messages } from "primereact/messages";
import { Paginator } from "primereact/paginator";
import { useHistory } from "react-router";
import { Panel } from "primereact/panel";
import { BreadCrumb } from "primereact/breadcrumb";
import { Dropdown } from "primereact/dropdown";
import * as constants from "../app_utils/constants/Constants";
import { HOME_ROUTE_PATH, SEND_EMAIL_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { PrimeIcons } from "primereact/api";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { generalStatusBodyTemplate } from "../app_utils/utils/Utils";
import { paginatorTemplate } from "../app_utils/components/PaginatorTemplate";
import { filtersHeadertemplate } from "../app_utils/components/FiltersPanelHeader";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: null },
  { label: "Sent", value: "Sent" },
  { label: "Failed", value: "Failed" },
];

const EmailReportView = () => {
  const [records, setRecords] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTermFilter, setSearchTermFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [first, setFirst] = useState<number>(0);
  const [limit, setLimit] = useState<number>(constants.MAXIMUM_RECORDS_PER_PAGE);
  const [summary, setSummary] = useState<any>({ total: 0, sent: 0, failed: 0, pending: 0 });
  const offsetRef = useRef<number>(0);

  const message = useRef<any>();
  const history = useHistory();

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      history.push(HOME_ROUTE_PATH);
    },
  };

  const breadcrumbItems = [
    {
      label: `Email Reports`,
      icon: PrimeIcons.ENVELOPE,
    },
  ];

  const getQueryParameters = () => {
    let searchParameters: any = { offset: offsetRef.current, limit: limit };
    if (searchTermFilter !== null) searchParameters.searchTerm = searchTermFilter;
    if (statusFilter !== null) searchParameters.status = statusFilter;
    return searchParameters;
  };

  const fetchRecordsFromServer = () => {
    setIsLoading(true);
    let searchParameters: any = getQueryParameters();

    new BaseApiServiceImpl("/api/v1/messages/ui")
      .getRequestWithJsonResponse(searchParameters)
      .then(async (response) => {
        setIsLoading(false);
        setRecords(response?.records);
        setTotalItems(response?.totalItems);
      })
      .catch((error) => {
        setIsLoading(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const fetchSummary = () => {
    let searchParameters: any = {};
    if (searchTermFilter !== null) searchParameters.searchTerm = searchTermFilter;
    if (statusFilter !== null) searchParameters.status = statusFilter;
    new BaseApiServiceImpl("/api/v1/messages/ui/summary")
      .getRequestWithJsonResponse(searchParameters)
      .then(async (response) => {
        setSummary(response?.data || {});
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchRecordsFromServer();
    fetchSummary();
  }, []);

  const onSubmitFilter = () => {
    offsetRef.current = 0;
    setFirst(0);
    fetchRecordsFromServer();
    fetchSummary();
  };

  const resetFilters = () => {
    setSearchTermFilter("");
    setStatusFilter(null);
    offsetRef.current = 0;
    setFirst(0);
    fetchRecordsFromServer();
    fetchSummary();
  };

  const rowIndexTemplate = (rowData: any, props: any) => {
    return (
      <React.Fragment>
        <span>{first + (props.rowIndex + 1)}</span>
      </React.Fragment>
    );
  };

  const onPageChange = (e: any) => {
    offsetRef.current = e.page * constants.MAXIMUM_RECORDS_PER_PAGE;
    setFirst(e.first);
    setLimit(constants.MAXIMUM_RECORDS_PER_PAGE);
    fetchRecordsFromServer();
  };

  const filterButtonsTemplate = (
    <>
      <div className="col-6 md:col-2 p-fluid" key="filterBtns">
        <Button
          icon={constants.ICON_SEARCH}
          className={constants.CSS_FILTER_SUBMIT_BUTTON}
          onClick={onSubmitFilter}
          loading={isLoading}
        />
        <Button
          icon={constants.ICON_REFRESH}
          className={constants.CSS_FILTER_RESET_BUTTON}
          onClick={resetFilters}
          loading={isLoading}
        />
      </div>
    </>
  );
  return (
    <div className="grid">
      <div className="col-6 flex justify-content-start flex-wrap">
        <BreadCrumb home={breadcrumbHome} model={breadcrumbItems} />
      </div>
      <div className="col-6 flex justify-content-end flex-wrap">
        <Button
          label={"Send Email"}
          icon={PrimeIcons.ENVELOPE}
          className="p-button-secondary p-button-sm"
          onClick={() => history.push(SEND_EMAIL_ROUTE_PATH)}
        />
      </div>
      <Messages ref={message} style={{ width: "100%" }} />
      <div className="col-12">
        <div className="grid">
          {[
            { label: "Total Emails", value: summary?.total, icon: PrimeIcons.ENVELOPE, color: "#6366f1" },
            { label: "Sent", value: summary?.sent, icon: PrimeIcons.CHECK_CIRCLE, color: "#22c55e" },
            { label: "Failed", value: summary?.failed, icon: PrimeIcons.TIMES_CIRCLE, color: "#ef4444" },
            { label: "Pending", value: summary?.pending, icon: PrimeIcons.CLOCK, color: "#f59e0b" },
          ].map((item) => (
            <div className="col-12 md:col-3" key={item.label}>
              <div className="card">
                <div className="flex align-items-center justify-content-between">
                  <div>
                    <span style={{ fontSize: "0.875rem", color: "#64748b" }}>{item.label}</span>
                    <h3 className="m-0 mt-2">{item.value ?? 0}</h3>
                  </div>
                  <i className={item.icon} style={{ fontSize: "2rem", color: item.color }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-12">
        <Panel headerTemplate={filtersHeadertemplate} toggleable>
          <div className="grid">
            <div className="col-12 md:col-3">
              <label htmlFor="searchTerm">Search</label>
              <input
                id="searchTerm"
                type="text"
                value={searchTermFilter || ""}
                onChange={(e) => setSearchTermFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSubmitFilter()}
                className="p-inputtext w-full mt-1"
                placeholder="Search by email, subject or body..."
              />
            </div>
            <div className="col-12 md:col-3">
              <label htmlFor="statusFilter">Status</label>
              <Dropdown
                id="statusFilter"
                value={statusFilter}
                options={STATUS_OPTIONS}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setStatusFilter(e.value)}
                placeholder="All Statuses"
                className="w-full mt-1"
              />
            </div>
            {filterButtonsTemplate}
          </div>
        </Panel>
      </div>
      <div className="col-12 card">
        <DataTable
          value={records}
          paginator={false}
          className="datatable-responsive"
          paginatorPosition="both"
          emptyMessage="No record found."
          loading={isLoading}
        >
          <Column
            field="Index"
            header="#"
            style={{ width: "70px" }}
            body={rowIndexTemplate}
          ></Column>
          <Column field="recipientEmail" header="Recipient" sortable />
          <Column field="subject" header="Subject" />
          <Column field="body" header="Body" />
          <Column field="sendDate" header="Date" />
          <Column field="lastErrorMessage" header="Error" />
          <Column
            header="Status"
            sortable
            body={(rowData: any) => generalStatusBodyTemplate(rowData?.status)}
          />
          {UserSessionUtils.isSuperAdmin() && (
            <Column field="gatewayName" header="Gateway" sortable />
          )}
        </DataTable>

        <Paginator
          first={first}
          rows={constants.MAXIMUM_RECORDS_PER_PAGE}
          totalRecords={totalItems}
          alwaysShow={true}
          onPageChange={onPageChange}
          template={paginatorTemplate}
        />
      </div>
    </div>
  );
};

export default EmailReportView;
